import React, { useState, useEffect } from 'react';
import { useI18n } from './i18n';
import { authFetch, isAuthenticated } from './auth';
import AttachmentList from './AttachmentList';
import { FOOD_PARAMS, WINE_PARAMS } from './codes';

const API_URL = window.location.hostname === 'localhost'
  ? 'http://localhost:8100/api'
  : '/api';

const withMax = (names, max) => names.map((name) => [name, max]);

// Comparison specs: [group key (catalog.group.<key>), [[field, max], ...]]. Labels come from <ns>.p.<field>.
const ONAV_COMPARISON_SPEC = [
  ['visual', withMax(['visual_hue', 'visual_intensity', 'visual_clarity'], 6)],
  ['olfactory', [['olfactory_cleanness', 6], ['olfactory_intensity', 8], ['olfactory_finesse', 8], ['olfactory_complexity', 8]]],
  ['gustatory', [['gustatory_cleanness', 6], ['gustatory_intensity', 8], ['gustatory_body', 8], ['gustatory_harmony', 8],
    ['gustatory_persistence', 8], ['gustatory_aftertaste', 6]]],
  ['totals', [['overall_impression', 8], ['total', 100]]],
];
const ASPI_COMPARISON_SPEC = [
  ['exam_visual', withMax(['clarity', 'transparency', 'color_intensity', 'consistency'], 5)],
  ['exam_olfactory', withMax(['olfactory_intensity', 'olfactory_complexity', 'olfactory_quality'], 5)],
  ['exam_gustatory', withMax(['gustatory_sugars', 'gustatory_alcohols', 'gustatory_polyols', 'gustatory_acids', 'gustatory_tannins',
    'gustatory_minerals', 'gustatory_balance', 'gustatory_intensity', 'gustatory_persistence', 'gustatory_quality'], 5)],
  ['final', [['harmony', 5], ['value_for_money', 5]]],
];
const PAIRING_COMPARISON_SPEC = [
  ['dish', withMax(FOOD_PARAMS, 10)],
  ['wine', withMax(WINE_PARAMS, 10)],
  ['outcome', [['score', 10]]],
];
// Parameters of a dish (food sheets of a dish, Mercadini method)
const FOOD_COMPARISON_SPEC = [['dish', withMax(FOOD_PARAMS, 10)]];

const fmt = (x) => (Number.isInteger(x) ? x : x.toFixed(1));

// Rows with a dedicated label; the others use <ns>.p.<field>
const SPECIAL_ROW = { total: 'catalog.p.total', score: 'catalog.p.score', final_score: 'catalog.p.final_score' };

// Comparison table: one column per sheet + min / average / max for every parameter.
// A row may carry `columnLabel` (highlighted column, e.g. the values being typed) and `excludeFromStats`.
function ComparisonTable({ title, ns, rows, spec, summary }) {
  const { t, formatDate } = useI18n();
  if (rows.length < 2) return null;
  const origin = (s) => (s.owner_id ? t('catalog.registered') : t('catalog.anonymous'));
  return (
    <details className="comparison">
      <summary>{summary || t('catalog.compare', { n: rows.length, title })}</summary>
      <div className="comparison-scroll">
        <table className="comparison-table">
          <thead>
            <tr>
              <th>{t('catalog.col_param')}</th>
              {rows.map((s, i) => (
                <th key={s.id} className={s.columnLabel ? 'current' : ''} title={`${formatDate(s.tasting_date)} ${s.taster || ''}`.trim()}>
                  {s.columnLabel ? <strong>{s.columnLabel}</strong> : (
                    <>
                      #{i + 1}<br /><small>{origin(s)}</small>
                      {(s.chef || s.venue) && <><br /><small>{[s.chef, s.venue].filter(Boolean).join(' · ')}</small></>}
                      {s.type === 'food' && s.tasting_date && <><br /><small>{formatDate(s.tasting_date)}</small></>}
                    </>
                  )}
                </th>
              ))}
              <th className="st">{t('catalog.col_min')}</th>
              <th className="st">{t('catalog.col_avg')}</th>
              <th className="st">{t('catalog.col_max')}</th>
            </tr>
          </thead>
          <tbody>
            {spec.map(([group, params]) => (
              <React.Fragment key={group}>
                <tr className="grp"><td colSpan={rows.length + 4}>{t(`catalog.group.${group}`)}</td></tr>
                {params.map(([key, max]) => {
                  const values = rows.filter((s) => !s.excludeFromStats).map((s) => s[key]).filter((v) => typeof v === 'number');
                  if (values.length === 0) return null;
                  const lo = Math.min(...values);
                  const hi = Math.max(...values);
                  const avg = values.reduce((a, b) => a + b, 0) / values.length;
                  return (
                    <tr key={key}>
                      <td>{t(SPECIAL_ROW[key] || `${ns}.p.${key}`)} <small>/{max}</small></td>
                      {rows.map((s) => (
                        <td key={s.id} className={`${s.columnLabel ? 'current ' : ''}${s[key] === hi && hi !== lo ? 'hi' : s[key] === lo && hi !== lo ? 'lo' : ''}`}>
                          {typeof s[key] === 'number' ? s[key] : '—'}
                        </td>
                      ))}
                      <td className="st">{fmt(lo)}</td><td className="st"><strong>{fmt(avg)}</strong></td><td className="st">{fmt(hi)}</td>
                    </tr>
                  );
                })}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </details>
  );
}

function WineDetail({ wineId, onBack }) {
  const { t, tc, lang, formatDate, formatNumber } = useI18n();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let abort = false;
    authFetch(`${API_URL}/wines/${wineId}/sheets`)
      .then((r) => (r && r.ok ? r.json() : Promise.reject(r && r.status)))
      .then((d) => { if (!abort) setData(d); })
      .catch((e) => { if (!abort) setError(e || true); });
    return () => { abort = true; };
  }, [wineId]);

  if (error) return <div className="catalog"><button className="back-btn" onClick={onBack}>{t('catalog.back')}</button><p>{t('catalog.err_wine')}</p></div>;
  if (!data) return <div className="catalog"><button className="back-btn" onClick={onBack}>{t('catalog.back')}</button><p>{t('ui.loading')}</p></div>;

  const wine = data.wine;
  const profiles = data.wine_profiles || [];            // absent when the API has no Mercadini wine sheets yet
  const pairingsByDish = Object.entries(data.pairings.reduce((acc, p) => {
    (acc[p.dish_name] = acc[p.dish_name] || []).push(p);
    return acc;
  }, {}));

  return (
    <div className="catalog">
      <button className="back-btn" onClick={onBack}>{t('catalog.back_wines')}</button>
      <div className="wine-card">
        <h2>{wine.name} {wine.vintage ? <span className="year">{wine.vintage}</span> : ''}</h2>
        {wine.producer && <p><strong>{t('catalog.producer')}:</strong> {wine.producer}</p>}
        <div className="wine-meta">
          {wine.classification && <span><strong>{t('catalog.class')}:</strong> {tc('classification', wine.classification)}</span>}
          {wine.wine_type && <span><strong>{t('catalog.type')}:</strong> {tc('wine_type', wine.wine_type)}</span>}
          {wine.production_area && <span><strong>{t('catalog.zone')}:</strong> {wine.production_area}</span>}
          {wine.abv != null && <span><strong>{t('catalog.alcohol')}:</strong> {formatNumber(wine.abv)}%</span>}
        </div>
      </div>

      <h3>{t('catalog.labels')}</h3>
      <AttachmentList ownerType="wines" ownerId={wine.id} canEdit={isAuthenticated()} />

      <p>
        <a className="submit-btn" style={{ textDecoration: 'none', display: 'inline-block' }}
          href={`${API_URL}/wines/${wine.id}/report/pdf?lang=${lang}`} target="_blank" rel="noopener noreferrer">
          {t('catalog.report_btn')}
        </a>
        <small className="report-hint"> {t('catalog.report_hint')}</small>
      </p>

      <h3>{t('catalog.onav_title', { n: data.onav_sheets.length })}</h3>
      {data.onav_sheets.length === 0 ? <p className="empty">{t('catalog.none_onav')}</p> : (
        <ul className="sheet-list">
          {data.onav_sheets.map((s) => (
            <li key={s.id} className="sheet-summary">
              <span>{formatDate(s.tasting_date) || '—'} · {s.taster || t('catalog.anonymous')}</span>
              <span className="score">{s.total}/100 {s.verdict && <em>({tc('verdict', s.verdict)})</em>}</span>
            </li>
          ))}
        </ul>
      )}
      <ComparisonTable title="ONAV" ns="onav" rows={data.onav_sheets} spec={ONAV_COMPARISON_SPEC} />

      <h3>{t('catalog.aspi_title', { n: data.aspi_sheets.length })}</h3>
      {data.aspi_sheets.length === 0 ? <p className="empty">{t('catalog.none_aspi')}</p> : (
        <ul className="sheet-list">
          {data.aspi_sheets.map((s) => (
            <li key={s.id} className="sheet-summary">
              <span>{formatDate(s.tasting_date) || '—'} · {s.taster || t('catalog.anonymous')}</span>
              <span className="score">
                {s.final_score != null ? `${formatNumber(s.final_score)}/100` : t('catalog.harmony', { n: s.harmony })}
              </span>
            </li>
          ))}
        </ul>
      )}
      <ComparisonTable title="ASPI" ns="aspi" rows={data.aspi_sheets} spec={ASPI_COMPARISON_SPEC} />

      {/* Mercadini wine sheets: the wine values each pairing saved as its own sheet (several tasters) */}
      <h3>{t('catalog.profile_title', { n: profiles.length })}</h3>
      {profiles.length === 0 ? <p className="empty">{t('catalog.none_profile')}</p> : (
        <ul className="sheet-list wine-profile-list">
          {profiles.map((s) => (
            <li key={s.id} className="sheet-summary">
              <span>{formatDate(s.tasting_date) || '—'} · {s.taster || (s.owner_id ? t('catalog.registered_user') : t('catalog.anonymous'))}</span>
              <span className="score">
                {['wine_body', 'wine_acidity', 'wine_tannicity', 'wine_alcohol', 'wine_pai']
                  .map((field) => `${t(`pairing.p.${field}`)} ${typeof s[field] === 'number' ? s[field] : '—'}`).join(' · ')}
              </span>
            </li>
          ))}
        </ul>
      )}

      <h3>{t('catalog.pairing_title', { n: data.pairings.length })}</h3>
      {data.pairings.length === 0 ? <p className="empty">{t('catalog.none_pairing')}</p> : (
        <ul className="sheet-list">
          {data.pairings.map((s) => (
            <li key={s.id} className="sheet-summary">
              <span>{s.dish_name} · {formatDate(s.tasting_date) || '—'}</span>
              <span className="score">
                {tc('outcome', s.pairing_outcome) || '—'}
                {s.score != null && <em> ({formatNumber(s.score)}/10)</em>}
              </span>
            </li>
          ))}
        </ul>
      )}
      {pairingsByDish.map(([dishName, rows]) => (
        <ComparisonTable key={dishName} title={`Mercadini · ${dishName}`} ns="pairing" rows={rows} spec={PAIRING_COMPARISON_SPEC} />
      ))}
    </div>
  );
}

function WineCatalog() {
  const { t, tc } = useI18n();
  const [query, setQuery] = useState('');
  const [wines, setWines] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  useEffect(() => {
    const controller = new AbortController();
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const url = `${API_URL}/wines${query ? `?q=${encodeURIComponent(query)}` : ''}`;
        const res = await fetch(url, { signal: controller.signal });
        if (res.ok) setWines(await res.json());
      } catch { /* aborted */ } finally { setLoading(false); }
    }, query ? 250 : 0);
    return () => { clearTimeout(timer); controller.abort(); };
  }, [query]);

  if (selectedId) return <WineDetail wineId={selectedId} onBack={() => setSelectedId(null)} />;

  return (
    <div className="catalog">
      <h2>{t('catalog.wines_title')}</h2>
      <div className="catalog-search">
        <input
          type="search"
          placeholder={t('catalog.wines_search')}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>
      {loading && <p className="empty">{t('ui.loading')}</p>}
      {!loading && wines.length === 0 && <p className="empty">{t('catalog.wines_none')}</p>}
      {wines.length > 0 && (
        <table className="catalog-table">
          <thead>
            <tr>
              <th>{t('catalog.th_name')}</th>
              <th>{t('catalog.th_year')}</th>
              <th>{t('catalog.producer')}</th>
              <th>{t('catalog.type')}</th>
              <th>{t('catalog.th_class')}</th>
            </tr>
          </thead>
          <tbody>
            {wines.map((w) => (
              <tr key={w.id} onClick={() => setSelectedId(w.id)}>
                <td><strong>{w.name}</strong></td>
                <td>{w.vintage || '—'}</td>
                <td>{w.producer || '—'}</td>
                <td>{tc('wine_type', w.wine_type) || '—'}</td>
                <td>{tc('classification', w.classification) || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export { ComparisonTable, FOOD_COMPARISON_SPEC };
export default WineCatalog;
